/**
 * A runsheet, drawn on the canvas.
 *
 * The chart has always been built from the runsheet rather than drawn
 * from nothing — an agent who adds a deed gets the box and the arrow
 * without touching the canvas, and that is the difference between this
 * and paying for a blank drawing tool. This is that build, targeting the
 * general canvas instead of the chart tables.
 *
 * THE SHAPE IS BIPARTITE: party → instrument → party. Not because it is
 * tidy but because it is what a landman draws and what the Lucid charts
 * this replaces actually contain. The instrument is a box of its own —
 * volume and page, date, what moved, over which land — so there is one
 * thing to click to read the deed, and so a conveyance with four
 * grantors is one instrument with four arrows into it rather than four
 * deeds that happen to share a name.
 *
 * IT MERGES, IT NEVER REPLACES. Running it again after finding six more
 * deeds must leave an hour of arranging exactly as it was: a box that is
 * already on the canvas keeps its position, its colour and anything
 * typed into it, and nothing is ever deleted. A build that could undo
 * somebody's work is a build nobody dares run.
 */
import { chartFromRunsheet, layoutParties, linkLabel } from '../titleChart.js';
import { makeShape, makeEdge } from './model.js';

// The size the real charts use, so a seeded box sits beside a
// hand-drawn one without either looking wrong.
export const PARTY = Object.freeze({ w: 160, h: 160 });
export const DEED = Object.freeze({ w: 178, h: 110 });

/** Everything an instrument box says, in the order a deed is read. */
export function deedText(link) {
  return [
    link.label || 'Conveyance',
    link.effective_date ? String(link.effective_date).slice(0, 10) : '',
    link.interest_conveyed,
    link.land,
    link.interest_reserved ? `Reserving ${link.interest_reserved}` : '',
  ].filter(Boolean).join('\n');
}

/**
 * Where a new party goes.
 *
 * Laid out by the same tier walk the chart uses — title flows down the
 * page, siblings spread sideways — then shifted clear of whatever is
 * already on the canvas, so a second build does not drop forty boxes on
 * top of the ones somebody arranged.
 */
function placements(parties, links, occupied) {
  const placed = layoutParties(parties, links);
  const lowest = occupied.length
    ? Math.max(...occupied.map((s) => s.y + s.h)) + 120
    : 0;
  const spot = new Map();
  for (const party of placed) {
    // A tier is deep enough to leave room for the instrument box in the
    // gap; the party layout already accounts for that.
    spot.set(party.match_key, { x: party.x, y: party.y + lowest });
  }
  return spot;
}

/**
 * Build the runsheet onto a document.
 *
 * Returns the new document and a count of what it added, because "added
 * nothing" and "added forty" want different sentences on screen and the
 * caller cannot tell them apart from the document alone.
 */
export function seedFromRunsheet(doc, rows, { land = '' } = {}) {
  const built = chartFromRunsheet(rows, { land });

  // What is already drawn, by what it was drawn FROM. A party is keyed
  // by its match key so two spellings of one man are one box; a
  // conveyance is keyed by its runsheet row, because one row is one
  // instrument however many people signed it.
  const haveParty = new Map();
  const haveDeed = new Map();
  for (const shape of doc.shapes) {
    if (shape.origin !== 'auto' || !shape.sourceId) continue;
    if (shape.sourceKind === 'deed') haveDeed.set(shape.sourceId, shape);
    else haveParty.set(shape.sourceId, shape);
  }

  const wanted = built.parties.filter((p) => !haveParty.has(p.match_key));
  const spot = placements(built.parties, built.links, doc.shapes);

  const shapes = [...doc.shapes];
  const edges = [...doc.edges];
  let z = doc.nextZ;
  let addedParties = 0;
  let addedDeeds = 0;

  const partyShape = new Map(haveParty);
  for (const party of wanted) {
    const at = spot.get(party.match_key) || { x: 0, y: 0 };
    const shape = makeShape({
      ...PARTY,
      ...at,
      text: party.label,
      // An estate reads differently from a living owner and the charts
      // colour it so. Everything else takes the default white.
      fill: party.kind === 'estate' ? '#f0e6d2' : '#ffffff',
      z,
      origin: 'auto',
      sourceId: party.match_key,
      sourceKind: 'party',
    });
    z += 1;
    shapes.push(shape);
    partyShape.set(party.match_key, shape);
    addedParties += 1;
  }

  for (const link of built.links) {
    if (haveDeed.has(link.runsheet_row_id)) continue;
    const grantors = [link.fromKey, ...(link.extraFromKeys || [])]
      .map((k) => partyShape.get(k)).filter(Boolean);
    const grantees = [link.toKey, ...(link.extraToKeys || [])]
      .map((k) => partyShape.get(k)).filter(Boolean);
    // A conveyance whose parties are not on the canvas is not drawable,
    // and drawing half of it would put an arrow into nothing.
    if (!grantors.length || !grantees.length) continue;

    // Between the two groups, which is where the eye looks for it.
    const mid = (list, key, size) => (
      list.reduce((sum, s) => sum + s[key] + (size ? s[size] / 2 : 0), 0) / list.length
    );
    const deed = makeShape({
      ...DEED,
      x: Math.round((mid(grantors, 'x', 'w') + mid(grantees, 'x', 'w')) / 2 - DEED.w / 2),
      y: Math.round(
        (Math.max(...grantors.map((s) => s.y + s.h)) + Math.min(...grantees.map((s) => s.y)))
        / 2 - DEED.h / 2,
      ),
      shapeType: 'text',
      fill: 'transparent',
      stroke: 'transparent',
      bold: true,
      fontSize: 11,
      text: deedText({ ...link, label: link.label || linkLabel({}) }),
      z,
      origin: 'auto',
      sourceId: link.runsheet_row_id,
      sourceKind: 'deed',
      documentId: link.document_id || null,
    });
    z += 1;
    shapes.push(deed);
    addedDeeds += 1;

    // One arrow per party, so a box a line can leave is a box that can
    // go on to convey separately.
    for (const from of grantors) {
      edges.push(makeEdge({
        from: { shapeId: from.id, side: 'bottom' },
        to: { shapeId: deed.id, side: 'top' },
        endCap: 'none',
        z,
        origin: 'auto',
        sourceId: link.runsheet_row_id,
      }));
      z += 1;
    }
    for (const to of grantees) {
      edges.push(makeEdge({
        from: { shapeId: deed.id, side: 'bottom' },
        to: { shapeId: to.id, side: 'top' },
        z,
        origin: 'auto',
        sourceId: link.runsheet_row_id,
      }));
      z += 1;
    }
  }

  return {
    doc: { shapes, edges, nextZ: z },
    added: { parties: addedParties, deeds: addedDeeds },
  };
}

/** What to tell somebody after a build. */
export function seedSummary({ parties, deeds }) {
  if (!parties && !deeds) return 'The diagram already has everything on the runsheet.';
  const bits = [];
  if (parties) bits.push(`${parties} part${parties === 1 ? 'y' : 'ies'}`);
  if (deeds) bits.push(`${deeds} conveyance${deeds === 1 ? '' : 's'}`);
  return `Added ${bits.join(' and ')}.`;
}
